/**
 * Shared Partner Sync behaviour for a single product line.
 *
 * Backs both the Products finance hub row and the System Design product-card header so the two
 * stay in lockstep: same visibility, same disabled rules, and the same modal routing (GoodLeap /
 * Enfin open the lead sync form with create hidden — their lead is created at agreement submit;
 * LightReach / IGS open the partner checklist so lead create/sync can run first).
 */
import { useCallback } from 'react';
import { Modal } from 'antd';
import { useProductsFinanceHubContext } from './ProductsFinanceHubContext';
import {
    hideBatteryLightreachPartnerSync,
    isEnfin,
    isGoodLeap,
    partnerKeyForSyncModal,
} from './productsFinanceHubHelpers';
import { hasProductDesignHardMiss, partnerSyncBlockedReasonFromItems } from './systemDesignMissingItems';
import { useSystemDesignMissingItems } from './useSystemDesignMissingItems';

export interface ProductPartnerSync {
    /** True when a Partner Sync control should be offered — requires a saved finance partner. */
    canSync: boolean;
    /**
     * True when the control is non-interactive (read-only / capability).
     * Design misses use `designBlocked` instead so click can still explain why.
     */
    syncDisabled: boolean;
    /** True when product design hard miss blocks Partner Sync (visual mute + alert on click). */
    designBlocked: boolean;
    /** Tooltip / notice when sync is blocked by missing product design. */
    syncBlockedReason: string | null;
    /**
     * True when Partner Sync should be hidden (not merely disabled) due to missing partner capability.
     * Currently: Enfin without `Enfin_AccountSearch` View.
     */
    hideForMissingCapability: boolean;
    /** Hide Partner Sync (Battery uses Solar when LR SB share one lead). */
    hidePartnerSync: boolean;
    /** Resolved partner display name for this product line. */
    partnerDisplayName: string;
    /** Open the correct partner sync flow for this product. */
    handlePartnerSync: () => void;
}

export interface ProductPartnerSyncOptions {
    /** Roof card header: partner may be assigned on the opportunity line without an active hub toggle. */
    allowInactiveProduct?: boolean;
}

export function useProductPartnerSync(
    productName: string,
    options?: ProductPartnerSyncOptions,
): ProductPartnerSync {
    const {
        theme,
        rows,
        getRow,
        partnerOptionsForProduct,
        isOpportunityInfoReadOnly,
        getCapabilityValue,
        hasUnsavedChanges,
        hasSavedFinancePartnerForProduct,
        noticeAPI,
        setLeadSyncFormOpen,
        setPartnerModal,
    } = useProductsFinanceHubContext();

    const systemDesignMissingItems = useSystemDesignMissingItems();
    const designBlocked = hasProductDesignHardMiss(productName, systemDesignMissingItems);
    const syncBlockedReason = designBlocked
        ? partnerSyncBlockedReasonFromItems(productName, systemDesignMissingItems)
        : null;

    const r = getRow(productName);
    const active = !!r?.activeFlag;
    const pNameRaw = String(r?.finance_Partner_Display_Name || '').trim();

    // Mirror ProductFinanceHubRow's partner readiness so the button appears exactly when the row's
    // own Partner Sync would.
    const partnerOptionsForThisProduct = partnerOptionsForProduct(productName);
    const partnerOptionsReady = partnerOptionsForThisProduct.length > 1;
    const partnerIsInOptions =
        !!pNameRaw &&
        pNameRaw.toLowerCase() !== 'select finance partner' &&
        partnerOptionsForThisProduct.some((o) => o.value === pNameRaw);
    const hasAssignedPartner =
        !!pNameRaw &&
        pNameRaw.toLowerCase() !== 'select finance partner' &&
        (partnerOptionsReady ? partnerIsInOptions : true);
    const hasPartner =
        (active || !!options?.allowInactiveProduct) && hasAssignedPartner;

    const isGoodLeapRow = isGoodLeap(pNameRaw);
    const isEnfinRow = isEnfin(pNameRaw);
    const isCashRow = pNameRaw.toLowerCase().includes('cash');
    const goodLeapFlowBlocked = isGoodLeapRow && getCapabilityValue('GoodLeap_AccountSearch') !== 'View';
    const enfinFlowBlocked = isEnfinRow && getCapabilityValue('Enfin_AccountSearch') !== 'View';

    // Sync pushes persisted data to the partner, so only offer it once this product's partner is
    // actually saved — a pending dropdown change has nothing to sync yet.
    // Enfin without account-search capability: hide (do not show a disabled control).
    const hideForMissingCapability = enfinFlowBlocked;
    const hidePartnerSync =
        productName === 'Battery' && hideBatteryLightreachPartnerSync(rows);
    const canSync =
        active &&
        hasPartner &&
        !isCashRow &&
        hasSavedFinancePartnerForProduct(productName) &&
        !hideForMissingCapability &&
        !hidePartnerSync;
    const syncDisabled = isOpportunityInfoReadOnly || !pNameRaw || goodLeapFlowBlocked;

    const handlePartnerSync = useCallback(() => {
        if (isOpportunityInfoReadOnly || goodLeapFlowBlocked || enfinFlowBlocked) return;
        if (designBlocked) {
            Modal.warning({
                title: 'Design required for Partner Sync',
                content:
                    syncBlockedReason ??
                    `Complete required steps before Partner Sync for ${productName || 'this product'}.`,
                okText: 'OK',
                centered: true,
                zIndex: 14000,
                className: `obd-partner-sync-design-blocked-modal obd-theme-${theme}`,
                rootClassName: `obd-theme-${theme}`,
            });
            return;
        }
        if (hasUnsavedChanges) {
            noticeAPI.warning({
                message: 'Unsaved Changes Found',
                description:
                    'You have unsaved changes in your product settings. Please save your progress before initiating a partner sync.',
                placement: 'topRight',
                className: `obd-notification obd-theme-${theme}`,
            });
            return;
        }
        if (isGoodLeapRow || isEnfinRow) {
            const syncKey = partnerKeyForSyncModal(r?.finance_Partner_Display_Name ?? '');
            setLeadSyncFormOpen(true, 'sync_existing', syncKey, { hideCreateLead: true, productName });
            return;
        }
        setPartnerModal({
            partnerName: partnerKeyForSyncModal(r?.finance_Partner_Display_Name ?? ''),
            productName,
        });
    }, [
        isOpportunityInfoReadOnly,
        goodLeapFlowBlocked,
        enfinFlowBlocked,
        designBlocked,
        syncBlockedReason,
        hasUnsavedChanges,
        noticeAPI,
        theme,
        isGoodLeapRow,
        isEnfinRow,
        r,
        setLeadSyncFormOpen,
        setPartnerModal,
        productName,
        rows,
    ]);

    return {
        canSync,
        syncDisabled,
        designBlocked,
        syncBlockedReason,
        hideForMissingCapability,
        hidePartnerSync,
        partnerDisplayName: pNameRaw,
        handlePartnerSync,
    };
}
